<template>
  <Teleport to="body">
    <div
      v-if="showOverlay"
      class="guidance-overlay"
      :style="isPanelMode ? { zIndex: 1500 } : {}"
    >
      <template v-if="!stopModalVisible && !guidanceStore.isModalStep">
        <!-- Цель не найдена (например, корзина пуста и чекбокса в шапке нет) —
             затемнение остаётся сплошным, без «окна»: иначе можно уйти в другой
             раздел, и тур встанет в тупик (CAB-636). -->
        <GuidanceHighlightBackdrop
          v-if="!isHighlightInForeground"
          :target-rects="overlay.targetRects.value"
          :highlight-padding="overlay.highlightPadding"
          :pointer-events-none="backdropPointerEventsNone"
        />

        <div
          v-if="!hasActiveStep"
          class="guidance-overlay__blocker"
          @click.stop
        />

        <template v-if="hasActiveStep && isPanelMode">
          <div
            v-for="(style, idx) in panelBlockerStyles"
            :key="idx"
            class="guidance-overlay__blocker"
            :style="style"
            @click.stop
          />
        </template>

        <template v-if="hasActiveStep && currentConfig?.advanceMode === 'interact'">
          <div
            v-for="(style, idx) in interactBlockerStyles"
            :key="idx"
            class="guidance-overlay__blocker"
            :style="style"
            @click.stop
          />
        </template>

        <div
          v-if="hasActiveStep && currentConfig?.advanceMode === 'button'"
          class="guidance-overlay__blocker"
          @click.stop
        />

        <div
          v-if="hasActiveStep && currentConfig?.advanceMode === 'click' && clickTargetStyle"
          class="guidance-overlay__blocker"
          @click.stop
        />
        <div
          v-if="hasActiveStep && currentConfig?.advanceMode === 'click' && clickTargetStyle"
          class="guidance-overlay__click-target"
          :style="clickTargetStyle"
          @click="handleClickTarget"
        />
      </template>

      <template v-if="stopModalVisible">
        <svg
          class="guidance-overlay__backdrop"
          width="100%"
          height="100%"
        >
          <defs>
            <mask id="guidance-stop-mask">
              <rect
                x="0"
                y="0"
                width="100%"
                height="100%"
                fill="white"
              />
              <rect
                v-if="sidebarHighlightRect"
                :x="sidebarHighlightRect.left - overlay.highlightPadding"
                :y="sidebarHighlightRect.top - overlay.highlightPadding"
                :width="sidebarHighlightRect.width + overlay.highlightPadding * 2"
                :height="sidebarHighlightRect.height + overlay.highlightPadding * 2"
                :rx="8"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            style="fill: var(--guidance-overlay-fill, rgba(0, 0, 0, 0.42))"
            mask="url(#guidance-stop-mask)"
          />
        </svg>
      </template>
    </div>
  </Teleport>

  <!-- Информационный слой тура — окно тура, стрелка, приветствие/финал,
       подтверждение остановки — отдельным teleport'ом ВНЕ .guidance-overlay
       (z-index 500, намеренно ниже поповеров приложения, см. комментарий в
       guidance-overlay.styl). Собственный z-index ребёнка не помогает выйти
       из чужого стекинг-контекста: окно тура рендерилось позади открытого
       модального окна (например, карточки поставщика на шаге 4) и было не
       видно. Здесь — свой контекст с высоким z-index, окно тура и кнопки в
       нём всегда доступны, независимо от того, что ещё открыто поверх
       затемнения. Засветка/блокировка кликов (выше) остаётся ниже поповеров —
       это отдельная, уже решённая задача (не делать реальный UI недоступным
       для клика под затемнением тура). Исключение — шаги с целью внутри
       самого попапа (highlightInForeground, GuidanceHighlightBackdrop здесь
       же): там подсветку нечем перекрывать снизу, ниже неё и так только
       страница, поэтому рамка/затемнение рисуются в этом же контейнере. -->
  <Teleport to="body">
    <div
      v-if="showOverlay"
      class="guidance-overlay-foreground"
    >
      <template v-if="!stopModalVisible && !guidanceStore.isModalStep">
        <GuidanceHighlightBackdrop
          v-if="!noTargetFallback && isHighlightInForeground"
          :target-rects="overlay.targetRects.value"
          :highlight-padding="overlay.highlightPadding"
          :pointer-events-none="backdropPointerEventsNone"
        />

        <GuidanceCurvedArrow
          v-if="hasActiveStep && arrowPoints && !tooltipOverlapsTarget"
          :fromX="arrowPoints.fromX"
          :fromY="arrowPoints.fromY"
          :toX="arrowPoints.toX"
          :toY="arrowPoints.toY"
        />

        <GuidancePanel
          v-if="
            (hasActiveStep || noTargetFallback) &&
            tooltipPosition &&
            currentConfig &&
            currentConfig.advanceMode === 'panel'
          "
          ref="tooltipComponentRef"
          :config="currentConfig"
          :position-style="tooltipStyle"
          :can-go-back="guidanceStore.canGoBack"
          @advance="handlePanelAdvance"
          @go-back="guidanceStore.goBack()"
        />

        <GuidanceTooltip
          v-else-if="(hasActiveStep || noTargetFallback) && tooltipPosition && tooltipConfig"
          ref="tooltipComponentRef"
          :config="tooltipConfig"
          :position-style="tooltipStyle"
          :can-go-back="guidanceStore.canGoBack"
          :ready-to-advance="guidanceStore.readyToAdvance"
          :kind="guidanceStore.activeKind"
          :total-steps="guidanceStore.totalSteps"
          @advance="guidanceStore.advance()"
          @stop="openStopModal"
          @go-back="guidanceStore.goBack()"
          @close="guidanceStore.skipAll()"
        />
      </template>

      <GuidanceWelcomeModal
        v-if="currentConfig?.advanceMode === 'welcome'"
        :visible="true"
        :kind="guidanceStore.activeKind"
        @start="guidanceStore.advance()"
        @skip="guidanceStore.pause()"
      />

      <GuidanceFinishModal
        v-if="currentConfig?.advanceMode === 'finish'"
        :visible="true"
        @finish="guidanceStore.advance()"
        @retake="guidanceStore.resetProgress()"
      />

      <GuidanceStopModal
        v-model:visible="stopModalVisible"
        @confirm="handleStopConfirm"
      />

      <DxPopup
        v-model:visible="warnModalVisible"
        :title="warnTexts.title"
        :width="420"
        :height="'auto'"
        :drag-enabled="false"
        :hide-on-outside-click="false"
        :wrapper-attr="{ class: 'guidance-stop-modal-wrapper' }"
      >
        <div class="guidance-stop-modal__text">
          {{ warnTexts.message }}
        </div>
        <div class="guidance-stop-modal__actions">
          <DxButton
            :text="warnTexts.stay"
            type="normal"
            styling-mode="outlined"
            @click="warnModalVisible = false"
          />
          <DxButton
            :text="warnTexts.continue"
            type="default"
            styling-mode="contained"
            @click="handleWarnContinue"
          />
        </div>
      </DxPopup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import type { GuidanceStepConfig } from "../models/guidance/GuidanceTypes";
import { ref, computed, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useGuidanceStore } from "../store/guidance";
import { useGuidanceOverlay } from "../common/composables/useGuidanceOverlay";
import { checkGuidanceCondition, computeOutsideBlockerRects } from "../common/helpers/guidanceHelpers";
import { DxPopup } from "devextreme-vue";
import DxButton from "devextreme-vue/button";
import GuidanceCurvedArrow from "./GuidanceCurvedArrow.vue";
import GuidanceHighlightBackdrop from "./GuidanceHighlightBackdrop.vue";
import GuidancePanel from "./GuidancePanel.vue";
import GuidanceTooltip from "./GuidanceTooltip.vue";
import GuidanceStopModal from "./GuidanceStopModal.vue";
import GuidanceWelcomeModal from "./GuidanceWelcomeModal.vue";
import GuidanceFinishModal from "./GuidanceFinishModal.vue";
import { useGuidanceMobileDrawer } from "../common/composables/useGuidanceMobileDrawer";

// Шаги, указывающие на пункты бокового меню, на телефоне открывают его:
// иначе подсветка встаёт за левым краем экрана, где панель запаркована
useGuidanceMobileDrawer();

const { t } = useI18n();
const guidanceStore = useGuidanceStore();
const router = useRouter();
const overlay = useGuidanceOverlay();

const sidebarHighlightRect = ref<DOMRect | null>(null);
const warnModalVisible = ref(false);
const tooltipComponentRef = ref<InstanceType<typeof GuidanceTooltip> | InstanceType<typeof GuidancePanel> | null>(null);

const stopModalVisible = computed({
  get: () => guidanceStore.stopModalVisible,
  set: (val: boolean) => {
    guidanceStore.stopModalVisible = val;
  },
});

const currentConfig = computed(() => guidanceStore.currentStepConfig);

const hasActiveStep = computed(() => !!currentConfig.value && overlay.targetRects.value.length > 0);
// Цель шага пропала насовсем (например, удалили все заказы вместе с чекбоксом
// в шапке) — тур не должен исчезать без следа: окно тура остаётся по центру без
// подсветки, а затемнение — сплошным (уйти в другой раздел нельзя, выход —
// «Назад» или «Пропустить шаг»). Если у шага есть текст для этого случая
// (noTargetTextKey), окно показывает его вместо обычного.
const noTargetFallback = computed(() => !!currentConfig.value && overlay.targetNotFound.value);
const tooltipConfig = computed<GuidanceStepConfig | null>(() => {
  const config = currentConfig.value;
  if (!config) return null;
  if (!noTargetFallback.value || !config.noTargetTextKey) return config;
  return { ...config, textKey: config.noTargetTextKey, hintKey: undefined, hintIcon: undefined };
});
const showOverlay = computed(() => guidanceStore.isActive || guidanceStore.stopModalVisible);

const tooltipPosition = computed(() => overlay.getTooltipPosition());

const tooltipStyle = computed(() => {
  const pos = tooltipPosition.value;
  if (!pos) {
    return {} as Record<string, string>;
  }
  return {
    position: "absolute",
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    zIndex: "10002",
    pointerEvents: "all",
  } as Record<string, string>;
});

const isPanelMode = computed(() => currentConfig.value?.advanceMode === "panel");

// Цель шага — внутри реального DxPopup (например, поля логина/пароля в
// карточке поставщика на шаге 4 онбординга): подсветка в обычном
// z-index 500 рендерится ПОД попапом и не видна вовсе. Для таких шагов
// (GuidanceStepConfig.highlightInForeground) переносим именно подсветку —
// затемнение и рамку — в контейнер окна тура (z-index 10010), не трогая
// обычные шаги — их подсветка остаётся под попапами приложения, чтобы не
// перекрывать клики по реальным дропдаунам/попапам.
const isHighlightInForeground = computed(() => !!currentConfig.value?.highlightInForeground);
const backdropPointerEventsNone = computed(
  () => hasActiveStep.value && (isPanelMode.value || currentConfig.value?.advanceMode === "interact"),
);

const warnConditionKey = ref<string | null>(null);

const warnTexts = computed(() => {
  if (warnConditionKey.value === "hasCreatedMarkup") {
    return {
      title: t("guidance.warnNoMarkupTitle"),
      message: t("guidance.warnNoMarkupMessage"),
      stay: t("guidance.warnNoMarkupStay"),
      continue: t("guidance.warnNoMarkupContinue"),
    };
  }
  return {
    title: t("guidance.warnNoSuppliersTitle"),
    message: t("guidance.warnNoSuppliersMessage"),
    stay: t("guidance.warnNoSuppliersStay"),
    continue: t("guidance.warnNoSuppliersContinue"),
  };
});

const arrowPoints = computed(() => overlay.getArrowPoints());

const clickTargetStyle = computed(() => overlay.getClickTargetStyle());

const panelBlockerStyles = computed(() => {
  if (!isPanelMode.value || overlay.targetRects.value.length === 0) return [];

  const rect = overlay.targetRects.value[0];
  const pad = overlay.highlightPadding;
  const left = rect.left - pad;
  const top = rect.top - pad;
  const right = rect.right + pad;
  const bottom = rect.bottom + pad;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  return [
    { position: "absolute", top: "0", left: "0", width: `${vw}px`, height: `${top}px` },
    { position: "absolute", top: `${bottom}px`, left: "0", width: `${vw}px`, height: `${vh - bottom}px` },
    { position: "absolute", top: `${top}px`, left: "0", width: `${left}px`, height: `${bottom - top}px` },
    {
      position: "absolute",
      top: `${top}px`,
      left: `${right}px`,
      width: `${vw - right}px`,
      height: `${bottom - top}px`,
    },
  ] as Record<string, string>[];
});

// Шаг "interact" — цель (или несколько раздельных целей) должна остаться
// кликабельной, а весь остальной фон заблокирован (CAB-636, замечание 5:
// фон не был заблокирован вовсе, клик мимо окна тура сворачивал шаг и
// обучение зависало). Сам SVG-фон визуально маскирует подсветку, но маска не
// снимает клики с замаскированной области — поэтому у него уже отключены
// pointer-events (см. backdropPointerEventsNone), а реальную блокировку берут
// на себя эти прямоугольники-заглушки, оставляющие дыры ровно под целями.
const interactBlockerStyles = computed(() => {
  if (currentConfig.value?.advanceMode !== "interact" || overlay.targetRects.value.length === 0) return [];

  const rects = computeOutsideBlockerRects(
    overlay.targetRects.value,
    overlay.highlightPadding,
    window.innerWidth,
    window.innerHeight,
  );

  return rects.map((rect) => ({
    position: "absolute",
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  })) as Record<string, string>[];
});

const tooltipOverlapsTarget = computed(() => {
  const pos = tooltipPosition.value;
  if (!pos || overlay.targetRects.value.length === 0) return false;

  const rect = overlay.targetRects.value[0];
  const tw = overlay.tooltipSize.value.width || 340;
  const th = overlay.tooltipSize.value.height || 220;
  const pad = overlay.highlightPadding;

  const targetLeft = rect.left - pad;
  const targetTop = rect.top - pad;
  const targetRight = rect.right + pad;
  const targetBottom = rect.bottom + pad;

  const tooltipRight = pos.x + tw;
  const tooltipBottom = pos.y + th;

  return pos.x < targetRight && tooltipRight > targetLeft && pos.y < targetBottom && tooltipBottom > targetTop;
});

function closeActiveDropdowns() {
  const backdrop = document.querySelector(".single-search__backdrop") as HTMLElement | null;
  backdrop?.click();
}

function handlePanelAdvance() {
  closeActiveDropdowns();
  const config = currentConfig.value;
  if (config?.warnCondition && !checkGuidanceCondition(config.warnCondition)) {
    warnConditionKey.value = config.warnCondition;
    warnModalVisible.value = true;
    return;
  }
  guidanceStore.advance();
}

function handleWarnContinue() {
  warnModalVisible.value = false;
  guidanceStore.advance();
}

function openStopModal() {
  guidanceStore.stopModalAction = "pause";
  guidanceStore.stopModalVisible = true;
}

function handleStopConfirm() {
  if (guidanceStore.stopModalAction === "skipAll") {
    guidanceStore.skipAll();
  } else {
    guidanceStore.pause();
  }
}

function handleClickTarget() {
  const config = currentConfig.value;
  if (!config) return;

  if (config.navigateOnClick) {
    router.push(config.navigateOnClick);
  }

  if (!config.advanceCondition) {
    guidanceStore.advance();
  }
}

watch(
  () => guidanceStore.stopModalVisible,
  (visible) => {
    if (visible) {
      guidanceStore.highlightedSidebarItems = ["guidance"];
      const el = document.querySelector('[data-tooltip="submodule-guidance"]');
      sidebarHighlightRect.value = el?.getBoundingClientRect() ?? null;
    } else {
      guidanceStore.highlightedSidebarItems = [];
      sidebarHighlightRect.value = null;
    }
  },
);

watch(
  () => tooltipComponentRef.value?.rootEl,
  async (el) => {
    overlay.tooltipRef.value = el ?? null;
    if (el) {
      await nextTick();
      overlay.updateTooltipSize();
    }
  },
);
</script>
